import { useState, useEffect, useRef } from "react";

// Global PDF mode state management with enhanced content tracking
let globalPDFMode = false;
let globalPDFProgress = 0;
const pdfModeListeners = new Set();
const pendingContentPromises = new Set();
const tableLoadingStates = new Map();

// Utility function to set PDF mode
export const setPDFMode = (enabled, progress = 0) => {
  globalPDFMode = enabled;
  globalPDFProgress = progress;

  if (!enabled) {
    // Clear all pending promises when PDF mode is disabled
    pendingContentPromises.clear();
    tableLoadingStates.clear();
  }

  pdfModeListeners.forEach((listener) => listener(enabled, progress));
};

// Utility function to get current PDF mode
export const getPDFMode = () => globalPDFMode;

// Utility function to get PDF progress
export const getPDFProgress = () => globalPDFProgress;

// Enhanced content waiting system
export const registerTableForPDF = (tableId, isLoading = true) => {
  if (globalPDFMode) {
    tableLoadingStates.set(tableId, isLoading);
  }
};

export const unregisterTableForPDF = (tableId) => {
  tableLoadingStates.delete(tableId);
};

// Enhanced React hook for PDF show/hide functionality
export const usePDFShowHide = (initialValue = false, options = {}) => {
  const {
    pdfDelay = 0,
    forceShowInPDF = true,
    onPDFModeChange = null,
    // New option for table identifier
    tableId = null,
  } = options;

  const [show, setShow] = useState(initialValue);
  const [pdfMode, setPdfModeState] = useState(globalPDFMode);
  const [pdfProgress, setPdfProgress] = useState(globalPDFProgress);
  const [pdfDelayComplete, setPdfDelayComplete] = useState(pdfDelay === 0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const listener = (enabled, progress = 0) => {
      setPdfModeState(enabled);
      setPdfProgress(progress);

      if (enabled && pdfDelay > 0) {
        setPdfDelayComplete(false);
        timeoutRef.current = setTimeout(() => {
          setPdfDelayComplete(true);
        }, pdfDelay);
      } else if (!enabled) {
        setPdfDelayComplete(pdfDelay === 0);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } else {
        setPdfDelayComplete(true);
      }

      if (onPDFModeChange) {
        onPDFModeChange(enabled, progress);
      }
    };

    pdfModeListeners.add(listener);

    return () => {
      pdfModeListeners.delete(listener);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (tableId) {
        unregisterTableForPDF(tableId);
      }
    };
  }, [pdfDelay, onPDFModeChange, tableId]);

  // Register table when PDF mode is enabled
  useEffect(() => {
    if (pdfMode && tableId) {
      registerTableForPDF(tableId, true);
    }
  }, [pdfMode, tableId]);

  let effectiveShow = show;
  if (pdfMode && forceShowInPDF && pdfDelayComplete) {
    effectiveShow = true;
  }

  return [
    effectiveShow,
    setShow,
    pdfMode,
    {
      pdfProgress,
      pdfDelayComplete,
      isPDFReady: pdfMode && pdfDelayComplete,
      tableId,
    },
  ];
};

// Enhanced content waiting function with better table detection
export const waitForPDFContent = async (timeout = 30000) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    let lastProgressTime = Date.now();
    const progressTimeout = 10000; // 10 seconds without progress = timeout

    const checkReady = () => {
      try {
        // Check for explicit loading indicators
        const explicitLoaders = document.querySelectorAll(
          '[data-pdf-loading="true"], .loading, .spinner, [data-loading="true"]'
        );

        // Check for empty tables that might still be loading
        const tables = document.querySelectorAll("table");
        let emptyTablesCount = 0;
        let tablesWithNoBodyRows = 0;

        tables.forEach((table) => {
          const tbody = table.querySelector("tbody");
          const rows = tbody ? tbody.querySelectorAll("tr") : [];

          // Count tables with no data rows (excluding header rows)
          const dataRows = Array.from(rows).filter((row) => {
            const cells = row.querySelectorAll("td");
            return (
              cells.length > 0 &&
              Array.from(cells).some(
                (cell) =>
                  cell.textContent.trim() !== "" &&
                  !cell.textContent.includes("No data") &&
                  !cell.textContent.includes("Loading")
              )
            );
          });

          if (dataRows.length === 0) {
            // Check if this table should have data by looking for associated components
            const tableContainer = table.closest(
              '[class*="table"], [class*="component"], [class*="content"]'
            );
            if (tableContainer) {
              tablesWithNoBodyRows++;
            }
          }
        });

        // Check for images that haven't loaded
        const images = document.querySelectorAll("img");
        const unloadedImages = Array.from(images).filter(
          (img) => !img.complete
        );

        // Check for any fetch requests in progress (if available)
        const activeRequests = window.activeXHRCount || 0;

        // Enhanced progress tracking
        const currentProgress = {
          explicitLoaders: explicitLoaders.length,
          tablesWithNoBodyRows,
          unloadedImages: unloadedImages.length,
          activeRequests,
          registeredTables: tableLoadingStates.size,
          loadingTables: Array.from(tableLoadingStates.values()).filter(Boolean)
            .length,
        };

        // Log progress for debugging
        console.log("PDF Content Check:", currentProgress);

        // Check if we've made progress
        const hasExplicitLoaders = explicitLoaders.length > 0;
        const hasEmptyTables = tablesWithNoBodyRows > 0;
        const hasUnloadedImages = unloadedImages.length > 0;
        const hasActiveRequests = activeRequests > 0;
        const hasLoadingTables = currentProgress.loadingTables > 0;

        // Content is ready if:
        // 1. No explicit loaders
        // 2. No empty tables (or we've waited long enough)
        // 3. All images loaded
        // 4. No active requests
        // 5. All registered tables are done loading
        const isReady =
          !hasExplicitLoaders &&
          !hasEmptyTables &&
          !hasUnloadedImages &&
          !hasActiveRequests &&
          !hasLoadingTables;

        if (isReady) {
          console.log("PDF Content Ready!");
          resolve();
        } else if (Date.now() - startTime > timeout) {
          console.warn("PDF Content timeout reached, proceeding anyway");
          reject(new Error("Timeout waiting for PDF content to load"));
        } else {
          // Check for progress timeout
          const progressMade =
            JSON.stringify(currentProgress) !==
            JSON.stringify(window.lastPDFProgress || {});
          if (progressMade) {
            lastProgressTime = Date.now();
            window.lastPDFProgress = currentProgress;
          } else if (Date.now() - lastProgressTime > progressTimeout) {
            console.warn(
              "No progress in PDF content loading, proceeding anyway"
            );
            resolve();
            return;
          }

          setTimeout(checkReady, 500); // Check every 500ms
        }
      } catch (error) {
        console.error("Error checking PDF content readiness:", error);
        resolve(); // Proceed anyway if there's an error
      }
    };

    checkReady();
  });
};

// Hook for tables to report their loading state
export const useTableLoadingForPDF = (tableId, isLoading) => {
  const [pdfMode] = usePDFShowHide();

  useEffect(() => {
    if (pdfMode && tableId) {
      registerTableForPDF(tableId, isLoading);
    }

    return () => {
      if (tableId) {
        unregisterTableForPDF(tableId);
      }
    };
  }, [pdfMode, tableId, isLoading]);
};

// Utility function to wait for specific API calls to complete
export const waitForAPICallsToComplete = async (maxWaitTime = 15000) => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const originalFetch = window.fetch;
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    let activeRequests = 0;

    // Track fetch requests
    window.fetch = function (...args) {
      activeRequests++;
      return originalFetch.apply(this, args).finally(() => {
        activeRequests--;
      });
    };

    // Track XHR requests
    XMLHttpRequest.prototype.open = function (...args) {
      activeRequests++;
      return originalXHROpen.apply(this, args);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      const xhr = this;
      const originalOnReadyStateChange = xhr.onreadystatechange;

      xhr.onreadystatechange = function (...args) {
        if (xhr.readyState === 4) {
          activeRequests--;
        }
        if (originalOnReadyStateChange) {
          return originalOnReadyStateChange.apply(this, args);
        }
      };

      return originalXHRSend.apply(this, args);
    };

    const checkComplete = () => {
      if (activeRequests === 0 || Date.now() - startTime > maxWaitTime) {
        // Restore original functions
        window.fetch = originalFetch;
        XMLHttpRequest.prototype.open = originalXHROpen;
        XMLHttpRequest.prototype.send = originalXHRSend;
        resolve();
      } else {
        setTimeout(checkComplete, 100);
      }
    };

    // Start checking after a small delay to catch initial requests
    setTimeout(checkComplete, 500);
  });
};
